import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ApiClient } from "./api/client.js";
import { SessionStore, browserStorage } from "./auth/session.js";
import { KnownRecords } from "./lib/known-records.js";
import { PortalProvider, type Portal } from "./app/context.js";
import { App } from "./app/App.jsx";
import "./styles.css";

/**
 * Wiring, and nothing else.
 *
 * Everything the app needs is constructed here and passed down as one object,
 * so a test can build the same three things against a real API and mount the
 * same tree. No module-level singletons: those are what make a component
 * impossible to render twice in one process.
 */

const session = new SessionStore(browserStorage());
const known = new KnownRecords(browserStorage());

const client = new ApiClient({
  credentials: () => session.credentials(),
  // A 401 means the token has gone — expired, revoked, or the server restarted
  // with a new secret. Clearing it here means the user lands on sign-in with an
  // explanation, rather than watching every panel on the screen fail in turn.
  onUnauthorised: (error) => {
    session.clear(error.message);
  },
});

const portal: Portal = { client, session, known };

const container = document.getElementById("root");
if (!container) {
  throw new Error("No #root element: index.html and main.tsx disagree.");
}

createRoot(container).render(
  <StrictMode>
    <PortalProvider value={portal}>
      <App />
    </PortalProvider>
  </StrictMode>,
);
